import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiChevronLeft,
  HiChevronRight,
  HiOutlineCalendarDays,
  HiOutlineArrowRight,
  HiOutlineQuestionMarkCircle,
  HiOutlineClipboardDocumentList,
  HiOutlineCpuChip,
  HiOutlineTag,
  HiChevronDown,
  HiChevronUp,
  HiOutlineArrowPath,
  HiOutlinePencilSquare,
  HiOutlineExclamationTriangle,
  HiOutlineClock,
  HiOutlinePower,
} from "react-icons/hi2";
import { authService } from "../../../services";
import pmStandardService from "../../../services/pmStandardService";
import machineService from "../../../services/machineService";
import plantService from "../../../services/plantService";
import lineService from "../../../services/lineService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SharedModal from "../../../components/common/SharedModal";
import PageLoading from "../../../components/common/PageLoading";
import "./PmCalendar.css";

const PmTaskCard = ({ task, selectedDate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tDate = new Date(selectedDate);
  tDate.setHours(0, 0, 0, 0);
  const isPast = tDate.getTime() < today.getTime();
  const isFuture = tDate.getTime() > today.getTime();

  return (
    <div
      className={`task-card-v3 priority-${task.priorityName?.toLowerCase() || "medium"} ${isExpanded ? "expanded" : ""} ${isRedirecting ? "is-redirecting" : ""}`}
    >
      <div
        className="card-inner"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: "pointer" }}
      >
        <div className="card-top">
          <div className="machine-path">
            <HiOutlineCpuChip className="path-icon" />
            <strong>Machine:</strong> {task.machineName}
            {task.subassemblyName && <span> / {task.subassemblyName}</span>}
          </div>
          <div className="header-actions">
            <div className="task-code-bubble">
              <HiOutlineTag /> {task.standardCode || "-"}
            </div>
            <button className="expand-toggle-btn">
              {isExpanded ? <HiChevronUp /> : <HiChevronDown />}
            </button>
          </div>
        </div>

        <h4 className="card-title">
          <span className="what-label">TASK : </span> {task.standardName}
        </h4>

        <div className="card-badges">
          <span className="badge-freq">{task.frequency}</span>
          {task.priorityName && (
            <span
              className={`badge-priority ${task.priorityName.toLowerCase()}`}
            >
              {task.priorityName}
            </span>
          )}
          <span className={`badge-status ${task.status.toLowerCase()}`}>
            {task.status}
          </span>
          {task.minutes != null && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f8fafc', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '500', color: '#475569', border: '1px solid #e2e8f0; border-color: var(--border-primary)' }}>
              <HiOutlineClock size={12} /> {task.minutes} mins
            </span>
          )}
          <span style={{
            display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '500',
            background: task.isMachineShutdownRequired ? '#fef2f2' : '#f8fafc',
            color: task.isMachineShutdownRequired ? '#991b1b' : '#475569',
            border: `1px solid ${task.isMachineShutdownRequired ? '#fee2e2' : '#e2e8f0; border-color: var(--border-primary)'}`
          }}>
            <HiOutlinePower size={12} color={task.isMachineShutdownRequired ? '#dc2626' : '#64748b'} />
            {task.isMachineShutdownRequired ? 'Shutdown Required' : 'No Shutdown'}
          </span>
        </div>

        {isExpanded && (
          <div className="instruction-grid animate-fade-in">
            {task.why && (
              <div className="instruction-msg why">
                <div className="msg-header">
                  <HiOutlineQuestionMarkCircle /> Why?
                </div>
                <p>{task.why}</p>
              </div>
            )}

            {task.how && (
              <div className="instruction-msg how">
                <div className="msg-header">
                  <HiOutlineClipboardDocumentList /> How?
                </div>
                <p>{task.how}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card-footer">
        {task.status !== 'Completed' && isPast ? (
          <button className="btn-complete-task disabled-missed" disabled>
            <HiOutlineExclamationTriangle /> Missed
          </button>
        ) : (task.status !== 'Completed' && isFuture) || (task.status === 'Completed' && isFuture) ? (
          <button className="btn-complete-task disabled-future" disabled>
            <HiOutlineCalendarDays /> Upcoming
          </button>
        ) : (
          <button
            className={`btn-complete-task ${isRedirecting ? "is-redirecting" : ""}`}
            disabled={isRedirecting}
            onClick={(e) => {
              e.stopPropagation();
              setIsRedirecting(true);
              const y = selectedDate.getFullYear();
              const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
              const d = String(selectedDate.getDate()).padStart(2, '0');
              const dateStr = `${y}-${m}-${d}`;
              navigate(
                `/home/pm/operator-tasks?standardId=${task.standardId}&date=${dateStr}`,
                {
                  state: { task, edit: task.status === 'Completed' },
                },
              );
            }}
          >
            {isRedirecting ? (
              <>
                Redirecting <HiOutlineArrowPath className="spin-icon" />
              </>
            ) : task.status === 'Completed' ? (
              <>
                Edit Execution <HiOutlinePencilSquare />
              </>
            ) : (
              <>
                Complete <HiOutlineArrowRight />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const PmCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [summaryData, setSummaryData] = useState([]);
  const [selectedDayTasks, setSelectedDayTasks] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Master Data State
  const [machines, setMachines] = useState([]);
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);
  const [maintenanceWindows, setMaintenanceWindows] = useState([]);

  // Filter State
  const [filters, setFilters] = useState({
    plantId: "",
    lineId: "",
    machineId: "",
    frequency: "",
  });

  const tenantId = authService.getTenantId();
  const cacheRef = useRef({});

  const fetchMasterData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        machineService.getAllMachines(),
        pmStandardService.getMaintenanceWindows(tenantId),
        plantService.getAllPlants(),
        lineService.getAllLines(),
      ]);

      if (results[0].status === "fulfilled") setMachines(results[0].value);
      if (results[1].status === "fulfilled")
        setMaintenanceWindows(results[1].value);
      if (results[2].status === "fulfilled") setPlants(results[2].value);
      if (results[3].status === "fulfilled") setLines(results[3].value);

      results.forEach((res, idx) => {
        if (res.status === "rejected") {
          console.warn(`Master data fetch ${idx} failed:`, res.reason);
        }
      });

      // If exactly one plant and nothing selected, auto-select it
      if (results[2].status === "fulfilled") {
          const plantsData = results[2].value;
          // Auto-select plant if only 1 exists
          if (plantsData?.length === 1) {
            setFilters(prev => ({ ...prev, plantId: plantsData[0].plantId }));
          }
      }
    } catch (error) {
      console.error("Critical error fetching master data:", error);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  const fetchSummaryData = useCallback(async () => {
    const startOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    );
    const endOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
    );

    // Sanitize filters to remove empty strings
    const sanitizedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== "" && v !== null),
    );

    const cacheKey = `${startOfMonth.toISOString()}-${endOfMonth.toISOString()}-${JSON.stringify(sanitizedFilters)}`;

    // Stale-while-revalidate: If we have cache, use it immediately
    if (cacheRef.current[cacheKey]) {
      setSummaryData(cacheRef.current[cacheKey]);
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const data = await pmStandardService.getCalendarSummary(
        startOfMonth.toISOString(),
        endOfMonth.toISOString(),
        sanitizedFilters,
      );

      // Update cache and state with fresh data
      cacheRef.current[cacheKey] = data;
      setSummaryData(data);
    } catch (error) {
      console.error("Error fetching calendar summary:", error);
      if (!cacheRef.current[cacheKey]) {
        toast.error("Failed to load calendar data. Please check connection.");
      }
    } finally {
      setLoading(false);
    }
  }, [currentDate, filters]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => {
      const updated = {
        ...prev,
        [name]: ["machineId", "plantId", "lineId"].includes(name) && value !== "" ? parseInt(value, 10) : value,
      };

      // Cascade resets
      if (name === "plantId") {
        updated.lineId = "";
        updated.machineId = "";
      }
      if (name === "lineId") {
        updated.machineId = "";
      }

      return updated;
    });
  };

  const handleResetFilters = () => {
    setFilters({
      plantId: "",
      lineId: "",
      machineId: "",
      frequency: "",
    });
  };

  const handleDayClick = async (day) => {
    const clickedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
      12, 0, 0
    );

    // Format date as YYYY-MM-DD manually to avoid timezone shifts
    const y = clickedDate.getFullYear();
    const m = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const d = String(clickedDate.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    // 1. Reset state for the new day to avoid showing "stale" data from previously clicked day
    setSelectedDayTasks({
      day,
      tasks: [],
      date: clickedDate,
    });
    setModalLoading(true);
    setShowModal(true);

    // 2. Check cached summary data for instant feedback
    const summary = summaryData.find((s) => {
      const sDate = new Date(s.date);
      // Compare in local time parts to be safe
      return (
        sDate.getDate() === day &&
        sDate.getMonth() === currentDate.getMonth() &&
        sDate.getFullYear() === currentDate.getFullYear()
      );
    });

    if (summary && summary.tasks && summary.tasks.length > 0) {
      setSelectedDayTasks({
        day,
        tasks: summary.tasks,
        date: clickedDate,
      });
      setModalLoading(false);
    }

    try {
      // 3. Always fetch fresh data in background/foreground
      const sanitizedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== "" && v !== null),
      );

      const tasks = await pmStandardService.getDayTasks(dateStr, sanitizedFilters);

      setSelectedDayTasks({
        day,
        tasks: tasks || [],
        date: clickedDate,
      });
    } catch (error) {
      console.error("Error fetching tasks for day:", error);
      // Only toast error if we don't even have summary data to show
      if (!summary || !summary.tasks || summary.tasks.length === 0) {
        toast.error("Failed to fetch tasks for this day.");
      }
    } finally {
      setModalLoading(false);
    }
  };

  const renderHeader = () => {
    const month = currentDate.toLocaleString("default", { month: "long" });
    const year = currentDate.getFullYear();

    const lineOptions = filters.plantId 
      ? lines.filter(l => l.plantId === filters.plantId) 
      : lines;

    const machineOptions = filters.lineId
      ? machines.filter(m => m.lineId === filters.lineId)
      : filters.plantId
        ? machines.filter(m => m.line && m.line.plantId === filters.plantId)
        : machines;

    return (
      <div className="calendar-header">
        <div className="header-left">
          <HiOutlineCalendarDays className="header-icon" />
          <div className="title-wrapper">
            <h2>PM Calendar</h2>
            <span className="current-date-label">
              {month} {year}
            </span>
          </div>
        </div>

        <div className="header-filters">
          <div className="filter-item">
            <label>Plant</label>
            <select
              name="plantId"
              value={filters.plantId}
              onChange={handleFilterChange}
            >
              <option value="">All Plants</option>
              {plants.map((p) => (
                <option key={p.plantId} value={p.plantId}>
                  {p.plantName}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Line</label>
            <select
              name="lineId"
              value={filters.lineId}
              onChange={handleFilterChange}
            >
              <option value="">All Lines</option>
              {lineOptions.map((l) => (
                <option key={l.lineId} value={l.lineId}>
                  {l.lineName}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Machine</label>
            <select
              name="machineId"
              value={filters.machineId}
              onChange={handleFilterChange}
            >
              <option value="">All Machines</option>
              {machineOptions.map((m) => (
                <option key={m.machineId} value={m.machineId}>
                  {m.machineName}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Frequency</label>
            <select
              name="frequency"
              value={filters.frequency}
              onChange={handleFilterChange}
            >
              <option value="">All Frequencies</option>
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Half Yearly">Half Yearly</option>
              <option value="Yearly">Yearly</option>
              <option value="No of Days">No of Days</option>
            </select>
          </div>

          <button
            className="reset-filters-btn"
            onClick={handleResetFilters}
            title="Reset Filters"
          >
            <HiOutlineArrowPath />
          </button>
        </div>

        <div className="header-right">
          <div className="nav-controls">
            <button className="nav-btn" onClick={handlePrevMonth}>
              <HiChevronLeft />
            </button>
            <button className="nav-btn" onClick={handleNextMonth}>
              <HiChevronRight />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    // Previous month padding
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`prev-${i}`} className="calendar-day padding"></div>);
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const dayDate = new Date(year, month, d);

      const summary = summaryData.find((s) => {
        const sDate = new Date(s.date);
        return (
          sDate.getDate() === d &&
          sDate.getMonth() === month &&
          sDate.getFullYear() === year
        );
      });

      // Check for maintenance windows
      const windowForDay = maintenanceWindows.find((w) => {
        const wStart = new Date(w.startTime);
        const wEnd = new Date(w.endTime);
        const startDay = new Date(wStart).setHours(0, 0, 0, 0);
        const endDay = new Date(wEnd).setHours(23, 59, 59, 999);
        const checkDay = new Date(dayDate).getTime();
        return checkDay >= startDay && checkDay <= endDay;
      });

      const isToday = new Date().toDateString() === dayDate.toDateString();

      days.push(
        <div
          key={d}
          className={`calendar-day ${isToday ? "today" : ""} ${windowForDay ? "maint-window" : ""}`}
          onClick={() => handleDayClick(d)}
        >
          <div className="day-header">
            <span className="day-number">{d}</span>
            {windowForDay && (
              <span
                className="window-indicator"
                title={windowForDay.windowName}
              >
                Window
              </span>
            )}
          </div>

          {summary && (
            <>
              <div className="day-summary">
                {summary.pending > 0 && (
                  <div className="summary-badge pending">{summary.pending}</div>
                )}
                {summary.completed > 0 && (
                  <div className="summary-badge completed">
                    {summary.completed}
                  </div>
                )}
                {summary.overdue > 0 && (
                  <div className="summary-badge overdue">{summary.overdue}</div>
                )}
                {summary.criticalCount > 0 && (
                  <div
                    className="summary-badge critical"
                    title="Critical Tasks"
                  >
                    {summary.criticalCount}!
                  </div>
                )}
              </div>

              {summary.tasks && summary.tasks.length > 0 && (
                <div className="day-tasks-preview">
                  {summary.tasks.slice(0, 2).map((task, idx) => (
                    <div
                      key={idx}
                      className={`task-preview-item freq-${task.frequency?.toLowerCase().replace(/ /g, "-")}`}
                      title={`${task.standardName} (${task.frequency})`}
                    >
                      {task.standardName}
                    </div>
                  ))}
                  {summary.tasks.length > 2 && (
                    <div className="more-tasks-count">
                      +{summary.tasks.length - 2} more
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>,
      );
    }

    return days;
  };

  return (
    <div className="pagecontainer">
      {renderHeader()}

      <div className="calendar-grid">
        <div className="calendar-weekdays">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="weekday-label">
              {day}
            </div>
          ))}
        </div>
        <div className="calendar-days-container">
          {loading ? (
            <div className="loading-state">
              <PageLoading message="Loading schedule..." />
            </div>
          ) : (
            renderDays()
          )}
        </div>
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={`Tasks for ${selectedDayTasks?.date?.toLocaleDateString()}`}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowModal(false)}
            >
              Close
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          {modalLoading ? (
            <div className="modal-loading-tasks">
              <PageLoading message="Fetching task details..." />
            </div>
          ) : selectedDayTasks?.tasks?.length > 0 ? (
            <div className="tasks-list">
              {selectedDayTasks.tasks.map((task) => (
                <PmTaskCard
                  key={task.transactionId || task.standardId}
                  task={task}
                  selectedDate={selectedDayTasks?.date}
                />
              ))}
            </div>
          ) : (
            <div className="no-tasks-msg">
              No tasks scheduled for this day.
            </div>
          )}
        </div>
      </SharedModal>
    </div>
  );
};

export default PmCalendar;
